import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Copy, Share2, Gift, Users, TrendingUp, Award,
  Facebook, Twitter, Send, Mail, MessageCircle, QrCode,
  CheckCircle, Clock, DollarSign
} from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';

export const ReferralCentre = () => {
  const [referralInfo, setReferralInfo] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [rewardConfig, setRewardConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState(null);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [infoRes, referralsRes, rewardsRes, configRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/referral/info`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/referral/list?limit=20`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/referral/rewards?limit=20`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/referral/config`, { credentials: 'include' })
      ]);

      if (infoRes.ok) {
        const data = await infoRes.json();
        setReferralInfo(data);
        generateQRCode(data.referral_link);
      }

      if (referralsRes.ok) {
        const data = await referralsRes.json();
        setReferrals(data.referrals || []);
      }

      if (rewardsRes.ok) {
        const data = await rewardsRes.json();
        setRewards(data.rewards || []);
      }

      if (configRes.ok) {
        const data = await configRes.json();
        setRewardConfig(data);
      }
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = (link) => {
    const qr = new QRCodeStyling({
      width: 200,
      height: 200,
      data: link,
      dotsOptions: {
        color: "#4F46E5",
        type: "rounded"
      },
      backgroundOptions: {
        color: "#ffffff",
      },
      imageOptions: {
        crossOrigin: "anonymous",
        margin: 10
      }
    });
    setQrCode(qr);
  };

  const downloadQRCode = () => {
    if (qrCode) {
      qrCode.download({ name: "referral-qr", extension: "png" });
      toast.success('QR code downloaded');
    }
  };

  const copyToClipboard = async (text, label) => {
    if (!text) {
      toast.error(`${label} is not available`);
      return;
    }

    // Always use the reliable fallback method that works everywhere
    const fallbackCopy = () => {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        // Prevent scrolling to bottom
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success(`${label} copied to clipboard!`);
          return true;
        } else {
          toast.error(`Failed to copy ${label}. Please select and copy manually.`);
          return false;
        }
      } catch (err) {
        console.error('Fallback copy failed:', err);
        toast.error(`Failed to copy ${label}. Please select and copy manually.`);
        return false;
      }
    };

    // Try modern API first, but catch ALL errors including permission errors
    try {
      // Check if clipboard API is available and has permission
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        // Try to use the modern API
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard!`);
        return;
      } else {
        // API not available, use fallback
        fallbackCopy();
      }
    } catch (err) {
      // If modern API fails for ANY reason (including permissions), use fallback
      console.log('Modern clipboard API failed, using fallback:', err.message);
      fallbackCopy();
    }
  };

  const shareOnPlatform = (platform) => {
    if (!referralInfo) return;
    const link = referralInfo.share_links[platform];
    window.open(link, '_blank', 'width=600,height=400');
    toast.success(`Opening ${platform}...`);
  };

  if (loading) {
    return (
      <FocusedLayout title="Refer & Earn" description="Invite friends and earn rewards">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </FocusedLayout>
    );
  }

  const stats = referralInfo?.statistics || {};

  return (
    <FocusedLayout title="Refer & Earn" description="Invite friends and earn rewards">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Refer & Earn</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Share Matrise Network and earn rewards for every referral
            </p>
          </div>
          <Gift className="h-12 w-12 text-indigo-600" />
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Referrals</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {stats.total_referrals || 0}
                  </p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Active Referrals</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {stats.active_referrals || 0}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">
                    {stats.pending_referrals || 0}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Earned</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">
                    {stats.total_rewards_earned?.toFixed(0) || 0}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Milestone Progress & Badges */}
        <Card className="border-2 border-purple-200 dark:border-purple-800">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950">
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-600" />
              Referral Milestones & Badges
            </CardTitle>
            <CardDescription>Unlock rewards as you refer more friends</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-6">
              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium">Your Progress</span>
                  <span className="text-muted-foreground">
                    {stats.total_referrals || 0} / 100 referrals
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-purple-600 to-pink-600 h-3 rounded-full transition-all"
                    style={{ width: `${Math.min(((stats.total_referrals || 0) / 100) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Milestone Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Bronze Badge - 10 Referrals */}
                <div className={`text-center p-4 rounded-lg border-2 transition-all ${
                  (stats.total_referrals || 0) >= 10 
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-950' 
                    : 'border-gray-300 dark:border-gray-700 opacity-50'
                }`}>
                  <div className="text-4xl mb-2">🥉</div>
                  <p className="font-semibold text-sm">Bronze</p>
                  <p className="text-xs text-muted-foreground">10 Referrals</p>
                  {(stats.total_referrals || 0) >= 10 ? (
                    <Badge variant="default" className="mt-2">Unlocked!</Badge>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">
                      {10 - (stats.total_referrals || 0)} more to go
                    </p>
                  )}
                </div>

                {/* Silver Badge - 25 Referrals */}
                <div className={`text-center p-4 rounded-lg border-2 transition-all ${
                  (stats.total_referrals || 0) >= 25 
                    ? 'border-gray-400 bg-gray-50 dark:bg-gray-900' 
                    : 'border-gray-300 dark:border-gray-700 opacity-50'
                }`}>
                  <div className="text-4xl mb-2">🥈</div>
                  <p className="font-semibold text-sm">Silver</p>
                  <p className="text-xs text-muted-foreground">25 Referrals</p>
                  {(stats.total_referrals || 0) >= 25 ? (
                    <Badge variant="default" className="mt-2">Unlocked!</Badge>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">
                      {25 - (stats.total_referrals || 0)} more to go
                    </p>
                  )}
                </div>

                {/* Gold Badge - 50 Referrals */}
                <div className={`text-center p-4 rounded-lg border-2 transition-all ${
                  (stats.total_referrals || 0) >= 50 
                    ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950' 
                    : 'border-gray-300 dark:border-gray-700 opacity-50'
                }`}>
                  <div className="text-4xl mb-2">🥇</div>
                  <p className="font-semibold text-sm">Gold</p>
                  <p className="text-xs text-muted-foreground">50 Referrals</p>
                  {(stats.total_referrals || 0) >= 50 ? (
                    <Badge variant="default" className="mt-2">Unlocked!</Badge>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">
                      {50 - (stats.total_referrals || 0)} more to go
                    </p>
                  )}
                </div>

                {/* Platinum Badge - 100 Referrals */}
                <div className={`text-center p-4 rounded-lg border-2 transition-all ${
                  (stats.total_referrals || 0) >= 100 
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-950' 
                    : 'border-gray-300 dark:border-gray-700 opacity-50'
                }`}>
                  <div className="text-4xl mb-2">💎</div>
                  <p className="font-semibold text-sm">Platinum</p>
                  <p className="text-xs text-muted-foreground">100 Referrals</p>
                  {(stats.total_referrals || 0) >= 100 ? (
                    <Badge variant="default" className="mt-2">Unlocked!</Badge>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">
                      {100 - (stats.total_referrals || 0)} more to go
                    </p>
                  )}
                </div>
              </div>

              {/* Milestone Rewards Info */}
              <div className="bg-indigo-50 dark:bg-indigo-950 p-4 rounded-lg">
                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-indigo-600" />
                  Milestone Rewards
                </h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  {rewardConfig?.milestones?.length > 0 ? (
                    rewardConfig.milestones.map((milestone, idx) => (
                      <li key={idx}>
                        {milestone.badge_name && `${milestone.badge_name} `}
                        <strong>({milestone.referral_count}):</strong> {milestone.reward_amount} {milestone.reward_currency}
                        {milestone.description && ` - ${milestone.description}`}
                      </li>
                    ))
                  ) : (
                    <>
                      <li>🥉 <strong>Bronze (10):</strong> {rewardConfig?.trigger_amounts?.registration || 500} {rewardConfig?.default_currency || 'KCRD'} bonus + Special badge</li>
                      <li>🥈 <strong>Silver (25):</strong> {(rewardConfig?.trigger_amounts?.registration || 500) * 3} {rewardConfig?.default_currency || 'KCRD'} bonus + Priority support</li>
                      <li>🥇 <strong>Gold (50):</strong> {(rewardConfig?.trigger_amounts?.registration || 500) * 7} {rewardConfig?.default_currency || 'KCRD'} bonus + VIP status</li>
                      <li>💎 <strong>Platinum (100):</strong> {(rewardConfig?.trigger_amounts?.registration || 500) * 20} {rewardConfig?.default_currency || 'KCRD'} bonus + Lifetime benefits</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Code & Link */}
        <Card className="border-2 border-indigo-200 dark:border-indigo-800">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950 dark:to-purple-950">
            <CardTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5 text-indigo-600" />
              Your Referral Code
            </CardTitle>
            <CardDescription>Share this code or link with friends to earn rewards</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Referral Code */}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Referral Code
              </label>
              <div className="flex gap-2">
                <Input
                  value={referralInfo?.referral_code || ''}
                  readOnly
                  className="text-lg font-mono font-bold text-indigo-600 text-center"
                />
                <Button
                  onClick={() => copyToClipboard(referralInfo?.referral_code, 'Code')}
                  variant="outline"
                  size="icon"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Referral Link */}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Referral Link
              </label>
              <div className="flex gap-2">
                <Input
                  value={referralInfo?.referral_link || ''}
                  readOnly
                  className="text-sm"
                />
                <Button
                  onClick={() => copyToClipboard(referralInfo?.referral_link, 'Link')}
                  variant="outline"
                  size="icon"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Social Share Buttons */}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                Share on Social Media
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => shareOnPlatform('whatsapp')}
                  className="bg-green-600 hover:bg-green-700"
                  size="sm"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  onClick={() => shareOnPlatform('facebook')}
                  className="bg-blue-600 hover:bg-blue-700"
                  size="sm"
                >
                  <Facebook className="h-4 w-4 mr-2" />
                  Facebook
                </Button>
                <Button
                  onClick={() => shareOnPlatform('twitter')}
                  className="bg-sky-500 hover:bg-sky-600"
                  size="sm"
                >
                  <Twitter className="h-4 w-4 mr-2" />
                  X (Twitter)
                </Button>
                <Button
                  onClick={() => shareOnPlatform('telegram')}
                  className="bg-blue-500 hover:bg-blue-600"
                  size="sm"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Telegram
                </Button>
                <Button
                  onClick={() => shareOnPlatform('email')}
                  variant="outline"
                  size="sm"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email
                </Button>
              </div>
            </div>

            {/* QR Code */}
            <div className="flex items-center justify-center flex-col pt-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                QR Code
              </label>
              <div 
                ref={(node) => {
                  if (node && qrCode) {
                    node.innerHTML = '';
                    qrCode.append(node);
                  }
                }}
                className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white"
              />
              <Button onClick={downloadQRCode} variant="outline" size="sm" className="mt-3">
                <QrCode className="h-4 w-4 mr-2" />
                Download QR Code
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs: Referrals & Rewards */}
        <Tabs defaultValue="referrals" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="referrals">
              <Users className="h-4 w-4 mr-2" />
              My Referrals ({referrals.length})
            </TabsTrigger>
            <TabsTrigger value="rewards">
              <Award className="h-4 w-4 mr-2" />
              My Rewards ({rewards.length})
            </TabsTrigger>
          </TabsList>

          {/* Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>Your Referrals</CardTitle>
                <CardDescription>People who joined using your referral code</CardDescription>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      No referrals yet. Start sharing your code!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {referrals.map((ref, index) => (
                      <div
                        key={ref.referral_id || index}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {ref.display_name || 'User'}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Joined: {ref.joined_date || ref.registered_at?.substring(0, 10)}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant={ref.status === 'active' ? 'default' : 'secondary'}>
                            {ref.is_verified ? 'Verified' : ref.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards">
            <Card>
              <CardHeader>
                <CardTitle>Rewards History</CardTitle>
                <CardDescription>All rewards you've earned from referrals</CardDescription>
              </CardHeader>
              <CardContent>
                {rewards.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      No rewards yet. Refer friends to start earning!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rewards.map((reward, index) => (
                      <div
                        key={reward.reward_id || index}
                        className={`flex items-center justify-between p-4 border rounded-lg ${
                          reward.status === 'locked' ? 'bg-gray-50 dark:bg-gray-900 opacity-75' : ''
                        }`}
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            Level {reward.level} Reward
                            {reward.status === 'locked' && <span className="text-amber-600">🔒</span>}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {reward.trigger} • {reward.created_at?.substring(0, 10)}
                          </p>
                          {reward.status === 'locked' && reward.locked_reason && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              {reward.locked_reason}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-indigo-600">
                            +{reward.reward_amount} {reward.reward_type}
                          </p>
                          <Badge variant={
                            reward.status === 'paid' ? 'default' :
                            reward.status === 'locked' ? 'destructive' :
                            reward.status === 'pending' ? 'secondary' : 'outline'
                          }>
                            {reward.status === 'locked' ? '🔒 Locked' : reward.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </FocusedLayout>
  );
};

export default ReferralCentre;
